import { FC, memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";
import ClientOnly from "../shared-components/ClentOnly";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { LANG } from "~/utils/constant";

export interface RecentUpdate {
  id: number;
  model_key: string;
  url: string;
  text: string;
}

type PropsType = {
  data: RecentUpdate[];
};

const ITEMS_TO_SHOW = 5;

const ArrowIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#9a3412" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

interface UpdateItemProps {
  item: RecentUpdate;
  regular: string;
  normalInter: string;
}

const UpdateItem: FC<UpdateItemProps> = memo(({ item, regular, normalInter }) => {
  const { LocalizedLink } = useLocale();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr] items-start md:gap-2 group overflow-hidden py-2 ">
      <span className={cn("text-textColorPrimary font-semibold body_text_m", regular)}>{item.model_key}</span>

      <div className="relative flex items-center min-w-0">
        <div className="line-clamp-2">
          <LocalizedLink title={item.text} to={item.url} className="underline hover:text-orange-800 transition-colors lg:space-x-2">
            <span className="text-textColorPrimary font-semibold hidden lg:inline"> : </span>
            <span className={cn("text-textSecondaryColor hover:text-orange-800 body_text_m transition-all duration-75 ease-in", normalInter)}>
              {item.text}
            </span>
          </LocalizedLink>
        </div>
        <div className="ml-2 transition-transform duration-200 group-hover:text-orange-800 group-hover:translate-x-1 group-hover:opacity-100 opacity-0 flex-shrink-0">
          <ArrowIcon />
        </div>
      </div>
    </div>
  );
});

const RecentUpdates: FC<PropsType> = memo(({ data }) => {
  const { medium, regular, normalInter } = useFontStyle();
  const { t } = useTranslation();
  const [seeMore, setSeeMore] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const lang = useLanguageParams();
  const isGujarati = lang === LANG.gujarati;

  useEffect(() => {
    if (seeMore && componentRef.current) {
      const timeout = setTimeout(() => {
        componentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }, 1);

      return () => clearTimeout(timeout);
    }
  }, [seeMore]);

  const displayedItems = seeMore ? data : data.slice(0, ITEMS_TO_SHOW);
  const hasMore = data.length > ITEMS_TO_SHOW;
  const isEmpty = data.length === 0;

  const headerContent = (
    <div className="text-center">
      <div className="w-full flex flex-col items-center gap_btw_title_content">
        <p className={cn(isGujarati ? "home-page-heading-mar-guj" : "home-page-heading", medium)}>{t("home.recentUpdates")}</p>
        <span className={cn("sub_desc_text_color body_text_l text-center title_para_width ", normalInter)}>
          {t("home.recentUpdateDescription", {
            defaultValue: "Explore the recently updated collection of spiritual literature on our website.",
          })}
        </span>
      </div>
    </div>
  );

  const contentItems = (
    <>
      <div className="divide-y  divide-textHeadingColor divide-opacity-30">
        {displayedItems.map((item) => (
          <UpdateItem key={item.id} item={item} regular={regular} normalInter={normalInter} />
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-end pt-3">
          <button className={cn("text-textHeadingColor font-medium hover:underline", normalInter)} onClick={() => setSeeMore(!seeMore)}>
            {seeMore ? t("home.show_less") : t("home.show_more")}
          </button>
        </div>
      )}
    </>
  );

  const boxContent = (
    <div className="bg-cardBgColor backdrop-blur-sm rounded-lg p-4  border border-opacity-30 border-textHeadingColor overflow-hidden ">
      {isEmpty ? <p className={cn("text-textColor", regular)}>{t("home.no_recent_updates")}</p> : contentItems}
    </div>
  );

  return (
    <ClientOnly
      fallback={
        <div className="w-full flex flex-col gap-4">
          {headerContent}
          <div className="bg-cardBgColor backdrop-blur-sm rounded-lg p-4  border border-opacity-30 border-textHeadingColor overflow-hidden">
            {isEmpty ? <p className={cn("text-textColor", regular)}>{t("home.no_recent_updates")}</p> : contentItems}
          </div>
        </div>
      }
    >
      <div ref={componentRef} className="w-full flex flex-col gap-btw-text-and-content ">
        {headerContent}
        {boxContent}
      </div>
    </ClientOnly>
  );
});

export default RecentUpdates;
